import React from 'react';
import type { SingleValue } from 'react-select';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import Select from '@atlaskit/select';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expValEquals } from '@confluence/feature-experiments';

import { type GeneralAccessLevel, type GeneralAccessLevelPermission } from '../../../commonTypes';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../controller/Controller';
import type { ContentType } from '../../../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator';

const i18n = defineMessages({
	canViewOptionLabel: {
		id: 'share-and-restrict-dialog.general-access.permission.can-view',
		defaultMessage: 'Can view',
		description:
			'Label for the view option in the permissions dropdown in the general access section',
	},
	canEditOptionLabel: {
		id: 'share-and-restrict-dialog.general-access.permission.can-edit',
		defaultMessage: 'Can edit',
		description:
			'Label for the edit option in the permissions dropdown in the general access section',
	},
});

const uneditableGeneralAccessStyle = xcss({
	paddingInline: 'space.100',
	whiteSpace: 'nowrap',
});

export type GeneralAccessPermissionDropdownProps = {
	isLockedOut: boolean;
	generalAccessLevel: Exclude<GeneralAccessLevel, 'restricted'>;
	onCanViewCanEditChange: (becomeCanEdit: boolean) => void;
	isEditable: boolean;
	hasInheritedRestrictions: boolean;
	isDraft: boolean;
	isLivePage: boolean;
	contentType: ContentType;
	isSaving: boolean;
};

type GeneralAccessPermissionOption = SingleValue<{
	label: string;
	value: GeneralAccessLevelPermission;
}>;

export const GeneralAccessPermissionDropdown = ({
	isLockedOut,
	generalAccessLevel,
	onCanViewCanEditChange,
	isEditable,
	hasInheritedRestrictions,
	isDraft,
	isLivePage,
	contentType,
	isSaving,
}: GeneralAccessPermissionDropdownProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const options: GeneralAccessPermissionOption[] = [
		{
			label: formatMessage(i18n.canViewOptionLabel),
			value: 'view',
		},
		{
			label: formatMessage(i18n.canEditOptionLabel),
			value: 'edit',
		},
	];

	const handleSelectChange = (selectedOption: GeneralAccessPermissionOption) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'selected',
				actionSubject: 'option',
				actionSubjectId: 'GeneralAccessPermissionDropdown',
				attributes: {
					selectedOption: selectedOption?.value,
					hasInheritedRestrictions,
					isDraft,
					isLivePage,
					contentType,
				},
			},
		}).fire();

		onCanViewCanEditChange(selectedOption?.value === 'edit');
	};

	const value = options[generalAccessLevel === 'open-can-view' ? 0 : 1];

	return (
		<>
			{isEditable ? (
				<Select
					appearance="subtle"
					spacing="compact"
					isDisabled={
						isLockedOut ||
						(isSaving && expValEquals('persistence_specific_access_gate', 'cohort', 'experiment'))
					}
					isSearchable={false}
					options={options}
					value={value}
					onChange={handleSelectChange}
					label={value?.label}
				/>
			) : (
				<Box xcss={uneditableGeneralAccessStyle}>
					<FormattedMessage
						{...(generalAccessLevel === 'open-can-view'
							? i18n.canViewOptionLabel
							: i18n.canEditOptionLabel)}
					/>
				</Box>
			)}
		</>
	);
};
